import React, { useCallback, useEffect, useState } from 'react';
import './Comment.css';
import { useDispatch, useSelector } from 'react-redux';
import { SaveDataState } from '../../../redux/reducers/getSaveData/saveDataReducer';
import { CommentDataClass, CommentState } from '../../../redux/reducers/getComment/getCommentReducer';
import { getComment, postComment, putComment } from '../../../redux/actions/comment/comment';
import { useTranslation } from 'react-i18next';
import { SaveResultState } from '../../../redux/reducers/getSaveData/saveResultReducer';
import { useReadOnly } from '../../../utils/ReadOnlyContext';
import { setSaveResultSuccess } from '../../../redux/actions/saveData/saveData';
import { useNavigation } from '../../../utils/context/NavigationContext';
import { exactMatcher, FormError, getFormErrorByName, useFormErrorContext } from '../../../utils/context/FormErrorContext';
import ErrorMessages from '../../../components/common/ErrorMessages/ErrorMessages';
import { ProfileOverviewState } from '../../../redux/reducers/getProfile/getProfileOverviewReducer';
import { selectGetComment, selectProfileGetOverview, selectSaveData, selectSaveResult } from '../../../redux/selectors/exportSelectorPaths';

export default function Comment(): JSX.Element {
    const { t } = useTranslation('pie');
    const dispatch = useDispatch();
    const saveData: SaveDataState = useSelector(selectSaveData);
    const commentState: CommentState = useSelector(selectGetComment);
    const saveResultState: SaveResultState = useSelector(selectSaveResult);
    const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview);

    const [commentId, setCommentId] = useState(-1);
    const [comment, setComment] = useState("");
    const [internalCommentId, setInternalCommentId] = useState(-1);
    const [internalComment, setInternalComment] = useState("");
    const { isReadOnly } = useReadOnly()
    const campaignStatusCode = sessionStorage.getItem("campaignStatusCode")
    const [submitted, setSubmitted] = useState(false)
    const { direction } = useNavigation();
    const { errors, clearErrors, removeError } = useFormErrorContext();
    const [formErrors, setFormErrors] = useState<FormError[]>([]);
    const isInternalUser = (sessionStorage.getItem("isInternalUser") as string) === "true"

    useEffect(() => {
        if (errors.length > 0) {
            errors.forEach((error) => {
                if (error.primaryKey && error.primaryKey.split('.')[0] === "comment") {
                    const localKey = error.primaryKey.split('.')[1];
                    error.primaryKey = localKey;
                    // setErrorFields(prevData => ({ ...prevData, [localKey]: error.message }));
                    setFormErrors(prevData => [...prevData, error])
                }
            })
        } else {
            setFormErrors([]);
        }
    }, [errors]);

    useEffect(() => {
        if ((!commentState.loading && !commentState.success)
            || (commentState.data && commentState.data.length > 0 && commentState.data[0].campaignId !== Number(sessionStorage.getItem("campaignId") as string))) {
            dispatch(getComment(Number(sessionStorage.getItem("campaignId") as string)));
        }
        // eslint-disable-next-line
    }, []);

    const findComment = useCallback((cId: number) => {
        let commentData: CommentDataClass | undefined = undefined;
        if (commentState.data) {
            commentData = (commentState.data).find(({ commentId }) => commentId === cId);
        }
        return commentData;
    }, [commentState.data]);

    useEffect(() => {
        if (submitted && !saveResultState.loading) {
            setSubmitted(false)
            if (saveResultState.status === 200) {
                dispatch(getComment(Number(sessionStorage.getItem("campaignId") as string)));
                setFormErrors([]);
            }
        }
    }, [dispatch, saveResultState, submitted]);

    useEffect(() => {
        if (saveData.saving && !submitted && !isInternalUser) {
            setSubmitted(true)
            clearErrors()
            const triggerSpectral = isInternalUser || direction !== "backward"
            if (commentId < 0) {
                if (findComment(commentId)) {

                    dispatch(postComment(Number(sessionStorage.getItem("campaignId") as string), {
                        "comment": { ...(findComment(commentId)), "campaignStatusCode": campaignStatusCode },
                        "trigger_spectral": triggerSpectral,
                        "profile_type_code": profileOverview.profile.profile_type_code,
                        "is_internal": isInternalUser
                    }));
                } else {
                    dispatch(setSaveResultSuccess())
                    setSubmitted(false)
                }
            } else {
                dispatch(putComment(Number(sessionStorage.getItem("campaignId") as string), commentId, {
                    "comment": { ...(findComment(commentId)), "campaignStatusCode": campaignStatusCode },
                    "trigger_spectral": triggerSpectral,
                    "profile_type_code": profileOverview.profile.profile_type_code,
                    "is_internal": isInternalUser
                }));
            }
        }
        // eslint-disable-next-line
    }, [dispatch, saveData.saving, commentId, campaignStatusCode])

    useEffect(() => {
        if (saveData.saving && isInternalUser && !submitted) {
            setSubmitted(true)
            clearErrors()
            if (internalCommentId < 0) {
                if (findComment(internalCommentId)) {
                    dispatch(postComment(Number(sessionStorage.getItem("campaignId") as string), {
                        "comment": { ...(findComment(internalCommentId)), "campaignStatusCode": campaignStatusCode },
                        "trigger_spectral": true,
                        "profile_type_code": profileOverview.profile.profile_type_code,
                        "is_internal": isInternalUser
                    }));
                } else {
                    dispatch(setSaveResultSuccess())
                    setSubmitted(false)
                }
            } else {
                dispatch(putComment(Number(sessionStorage.getItem("campaignId") as string), internalCommentId, {
                    "comment": { ...(findComment(internalCommentId)), "campaignStatusCode": campaignStatusCode },
                    "trigger_spectral": true,
                    "profile_type_code": profileOverview.profile.profile_type_code,
                    "is_internal": isInternalUser
                }));
            }
        }
        // eslint-disable-next-line
    }, [dispatch, saveData.saving, internalCommentId, campaignStatusCode])

    useEffect(() => {
        if (!commentState.loading && commentState.data) {
            const commentData = commentState.data.find((comment) => comment.commentTypeCode === "EXT");
            if (commentData) {
                setCommentId(commentData.commentId ?? -1);
                setComment(commentData.commentText ?? "");
            }
            const internalCommentData = commentState.data.find((comment) => comment.commentTypeCode === "INT");
            if (internalCommentData) {
                setInternalCommentId(internalCommentData.commentId ?? -1);
                setInternalComment(internalCommentData.commentText ?? "");
            }
        } else if (!commentState.loading && !commentState.success) {
            setCommentId(-1);
            setComment("");
            setInternalCommentId(-1);
            setInternalComment("");
        }
    }, [commentState.data, commentState.loading, commentState.success]);

    const updateComment = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        removeError("commentText")
        removeError("CommentText") // Spectral returns inconsistent primary keys between profiles so just clearing both
        const comment = (e.currentTarget as HTMLTextAreaElement).value
        if (comment.length <= 5000) {
            const commentData = findComment(commentId);
            if (commentData) {
                commentData.commentText = comment;
            } else {
                const newComment = {
                    commentId: commentId,
                    commentDate: new Date().toISOString(),
                    commentTypeCode: "EXT",
                    userName: "",
                    profileId: Number(sessionStorage.getItem("profileId") as string),
                    campaignId: Number(sessionStorage.getItem("campaignId") as string),
                    commentText: comment,
                    deletedFlag: false
                }
                if (commentState.data) {
                    (commentState.data).push(newComment);
                } else {
                    commentState.data = [newComment];
                }
            }
            setComment(comment);
        }
    }

    const updateInternalComment = (comment: string) => {
        if (comment.length <= 5000) {
            const commentData = findComment(internalCommentId);
            if (commentData) {
                commentData.commentText = comment;
            } else {
                const newComment = {
                    commentId: internalCommentId,
                    commentDate: new Date().toISOString(),
                    commentTypeCode: "INT",
                    userName: "",
                    profileId: Number(sessionStorage.getItem("profileId") as string),
                    campaignId: Number(sessionStorage.getItem("campaignId") as string),
                    commentText: comment,
                    deletedFlag: false
                }
                if (commentState.data) {
                    (commentState.data).push(newComment);
                } else {
                    commentState.data = [newComment];
                }
            }
            setInternalComment(comment);
        }
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter') {
            e.stopPropagation()
        }
    }

    const isCommentDisabled = (commentTypeCode: string) => {
        if (isReadOnly) {
            return true
        }
        if (commentTypeCode === "EXT") {
            return isInternalUser
        } else {
            return false
        }
    }

    return (<>
        <div className='partial-width'>
            <div>
                <p className='strong'>
                    {
                        profileOverview.profile.profile_type_code === "AGS" ?
                            t("agsDescription")
                            :
                            t("commentDescription")
                    }
                </p>
                <div className='comment-error-section'>
                    <ErrorMessages errors={formErrors}></ErrorMessages>
                </div>
                <textarea id="comments" name="comments"
                    cols={80} rows={12} maxLength={1100} value={comment}
                    onKeyDown={(e) => handleKeyDown(e)}
                    onChange={(e) => updateComment(e)}
                    data-primary-key={getFormErrorByName(errors, "commentText", exactMatcher)?.primaryKey as string}
                    className={`comment-textbox ${formErrors.find(err => err.primaryKey === "commentText") ? 'form-error' : ''}`}
                    aria-label={t("commentDescription")}
                    disabled={isCommentDisabled("EXT")} />
            </div>
            {isInternalUser &&
                <>
                    <div>
                        <h2 className="internal-comments-heading">Internal comments</h2>
                        <textarea id="internal-comments" name="internal-comments"
                            cols={80} rows={12} maxLength={3500} value={internalComment}
                            onKeyDown={(e) => handleKeyDown(e)}
                            onChange={(e) => updateInternalComment((e.currentTarget as HTMLTextAreaElement).value)}
                            data-primary-key={getFormErrorByName(errors, "commentTextInternal", exactMatcher)?.primaryKey as string}
                            className={`comment-textbox internal-comment ${formErrors.find(err => err.primaryKey === "commentTextInternal") ? 'form-error' : ''}`}
                            aria-label="Internal comments"
                            disabled={false} />
                    </div>
                </>
            }
        </div>
    </>);
}
